/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';

import LoginContent from '../../Contents/LoginContent/LoginContent';
import { Box, Stack } from '@mui/material';

import LogoRMF from '../../../../assets/images/fullLogo.png';
import TwoFactorAuthModal from '../../../../components/TwoFactorAuthModal/TwoFactorAuthModal';
import { TwoFactorMethod } from '../../../../pages/user/login/services/post/login/interfaces';
import { useAuth } from '../../../../context/AuthContext/AuthContext';
import { tokenStorage } from '../../../../utils/tokenStorage';
import HeroContent from '../../Contents/HeroContent/HeroContent';
import RegisterContent from '../../Contents/RegisterContent/RegisterContent';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';
import { formPanelSx, heroStackSx } from './styles';
import { LAYOUT } from '../../styles/layout';

type AuthView = 'login' | 'register';

const Login = () => {
  const { verifyTwoFactor, twoFactorMethods, twoFactorEmail, clearTwoFactorState } = useAuth();

  const [activeView] = useState<AuthView>('login');
  const [show2FAModal, setShow2FAModal] = useState<boolean>(false);

  useEffect(() => {
    if (twoFactorMethods.length > 0 && twoFactorEmail) {
      setShow2FAModal(true);
    }
  }, [twoFactorMethods, twoFactorEmail]);

  const handle2FAVerify = async (method: TwoFactorMethod, code: string) => {
    try {
      await verifyTwoFactor(method, code);

      const storedAccessToken = tokenStorage.getAccessToken();

      if (storedAccessToken) {
        setShow2FAModal(false);

        const payload = JSON.parse(atob(storedAccessToken.split('.')[1]));
        if (payload.role === 'ROLE_ADMIN') {
          window.location.href = '/admin/dashboard';
        } else {
          window.location.href = '/';
        }
      } else {
        return;
      }
    } catch (error: any) {
      throw error;
    }
  };

  const handle2FACancel = () => {
    setShow2FAModal(false);
    clearTwoFactorState();
  };

  const handleTwoFactorRequired = (_email: string, _rememberMe: boolean) => {
    // The useEffect above will handle showing the modal when twoFactorMethods is set
  };

  return (
    <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden' }}>
      <Stack width="100%" height="100%">
        <Stack
          direction="row"
          height="100%"
          sx={{
            overflow: 'hidden',
            '@media (max-width:1023px)': { flexDirection: 'column' },
          }}
        >
          {/* HERO */}
          <Stack width={LAYOUT.heroWidth} height="100%" sx={heroStackSx}>
            <HeroContent />
          </Stack>

          {/* LOGIN / REGISTER */}
          <Stack
            justifyContent="center"
            alignItems="center"
            width={LAYOUT.formWidth}
            sx={formPanelSx}
          >
            <LanguageSelector />
            <Box component="img" src={LogoRMF} width="300px" height="auto" sx={{ mb: 2 }} />

            {activeView === 'login' ? (
              <LoginContent onTwoFactorRequired={handleTwoFactorRequired} />
            ) : (
              <RegisterContent />
            )}
          </Stack>
        </Stack>

        {/* 2FA Modal */}
        {twoFactorEmail && show2FAModal && (
          <TwoFactorAuthModal
            key={twoFactorMethods.join(',')}
            open={show2FAModal}
            email={twoFactorEmail}
            availableMethods={twoFactorMethods}
            onVerify={handle2FAVerify}
            onClose={handle2FACancel}
          />
        )}
      </Stack>
    </Box>
  );
};

export default Login;
